import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import GoBackButton from '@/common/components/GoBackButton';
import MaskedTextField from '@/common/components/MaskedTextField';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import Spinner from '@/common/components/Spinner'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { PermissionData } from '@/common/types/permissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateEstablishmentRole } from '@/features/establishmentRoles/hooks/useCreateEstablishmentRole'
import { useEstablishmentPermissions } from '@/features/establishmentRoles/hooks/useEstablishmentPermissions'
import { roleSchema, RoleValidationSchema } from '@/features/tcardRoles/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeHelpIcon, ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip'

const Create = () => {
  const navigate = useNavigate()

  const [search, setSearch] = useDebounce('')
  const [selectedPermissions, setSelectedPermissions] = useState<{ permission_id: string }[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)

  const {
    data: permissions,
    meta,
    isFetching: permissionsIsFetching,
    fetchNextPage,
  } = useEstablishmentPermissions<PermissionData[]>({
    filters: { search },
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RoleValidationSchema>({
    resolver: zodResolver(roleSchema),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateEstablishmentRolePermission = !isUserPermissionsPending ? hasUserPermission('Admin::Establishment::Roles::Create') : true

  const { mutate: createRole, isPending } = useCreateEstablishmentRole({
    permissions: selectedPermissions,
    onSuccess: () => {
      navigate('/establishment-roles')
    },
    onError: FormTransformers.errorsTransformer<RoleValidationSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<RoleValidationSchema> = (data) => createRole(data)

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || permissionsIsFetching) {
      return false
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    if (isToFetchNextPage()) {
      fetchNextPage()
    }
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateEstablishmentRolePermission ? 200 : 403}
    >
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton
            fallback={`/establishment-roles`}
            searchLastRouteFromFallback
          />
          <p className="text-white-400 font-normal">Cargos e permissões - Estabelecimento</p>
          <ChevronsRightIcon color="#ffffffb3" />
          <h2 className="font-bold">Novo cargo</h2>
        </div>
        <Button type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          {isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Confirmar'}
        </Button>
      </div>
      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
        <p className="text-lg font-bold">Cargo</p>
        <TextField
          label="Nome do cargo"
          markAsRequired
          maxLength={64}
          errorMessage={errors.display_name?.message || undefined}
          {...register('display_name')}
        />
        <TextField
          label="Descrição do cargo"
          markAsRequired
          maxLength={256}
          errorMessage={errors.description?.message || undefined}
          {...register('description')}
        />
        <MaskedTextField
          mask={'00'}
          label='Nível'
          placeholder='0'
          errorMessage={errors.level?.message || undefined}
          {...register('level')}
        />
        <Box className="flex flex-col gap-4">
          <p>Permissões</p>
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar permissões"
            onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
          />
          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 overflow-auto min-h-14 max-h-[calc(100vh-566px)]"
          >
            {permissions?.map((permission) => (
              <div key={permission.id} className="flex gap-2 items-center">
                <Checkbox
                  name={permission.display_name}
                  label={permission.display_name}
                  labelClassName="max-w-[360px] text-wrap break-words"
                  checked={selectedPermissions.find((value) => value.permission_id === permission.id) ? true : false}
                  onChange={(e) => {
                    if ((e.target as HTMLInputElement).checked) {
                      setSelectedPermissions([...selectedPermissions, { permission_id: permission.id }])
                    } else {
                      setSelectedPermissions(
                        selectedPermissions.filter((value) => value.permission_id !== permission.id),
                      )
                    }
                  }}
                />
                <div data-tooltip-id={permission.id} className="flex gap-1 items-center text-warning">
                  <BadgeHelpIcon strokeWidth={1} size={14} />
                  <p className="font-light text-[8px] italic">Informações</p>
                </div>
                <Tooltip id={permission.id}>{permission.description}</Tooltip>
              </div>
            ))}
          </div>
        </Box>
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
    </ErrorMessageWrapper>
  )
}

export default Create
